/**
 * What Allow on an opt-in card can grant (TMMAPP-0142 U3; carried from the U1 review): only what
 * this person may add to this session, so Allow never ends in `422 ASSISTANT_SCOPE_INVALID`.
 *
 * - **The owner's private session:** the entities they can scope now (`options.scopable`, never a
 *   shared virtual account, which is a session's only scope) and any category.
 * - **A sharer's private session** (one shared virtual account): no entity, and only the categories
 *   a sharer's session may turn on (`sharerAllowed`, or a key with `sharerTools`: `PLANNED` for the
 *   virtual account's plan only).
 * - **A shared session:** its scope is its one virtual account, so no entity; the categories a
 *   sharer may turn on, and only for whoever may manage it (`canManage`). A member's own data is
 *   never a session switch (`memberPrivate`): it is added through "Add my accounts".
 * - **A private room** (TMMAPP-0148 U1): a shared session's rules (no entity, no member's own data);
 *   the server never asks there for a key its shared session holds off.
 */
import type {
  AssistantOptions,
  AssistantScopeEntity,
  AssistantScopeRequest,
  AssistantSession,
} from '@/api/assistant';

/** Whether the session's categories follow the shared rules (a sharer's, shared or private room). */
export function sharedRules(session: AssistantSession): boolean {
  return session.visibility !== 'PRIVATE' || session.virtualAccountId !== null;
}

type SharerRules = {
  sharerAllowed: boolean;
  sharerTools: readonly string[];
  memberPrivate: boolean;
};

/** A category a sharer's session may turn on: sharer-allowed, or allowed for its sharer tools. */
export function sharerCanUse(option: SharerRules): boolean {
  return option.sharerAllowed || option.sharerTools.length > 0;
}

/** A category a sharer's or shared session reads through its sharer tools only (`PLANNED`). */
export function sharerToolsOnly(option: SharerRules): boolean {
  return !option.sharerAllowed && option.sharerTools.length > 0;
}

/** A category that can't be on in a shared session: not a sharer's, or a member's own data. */
export function blockedInShared(option: SharerRules): boolean {
  return !sharerCanUse(option) || option.memberPrivate;
}

export function grantableScope(
  session: AssistantSession,
  options: AssistantOptions | null,
  request: AssistantScopeRequest,
): AssistantScopeRequest {
  const registry = options?.categories ?? [];
  if (session.visibility === 'SHARED' && !session.canManage) {
    return { categories: [], entities: [], reason: request.reason };
  }
  const shared = sharedRules(session);
  const categories = request.categories.filter((key) => {
    const option = registry.find((entry) => entry.key === key);
    if (option === undefined) return false;
    if (!shared) return true;
    return session.visibility === 'PRIVATE' ? sharerCanUse(option) : !blockedInShared(option);
  });
  const entities: AssistantScopeEntity[] = shared
    ? []
    : request.entities.filter((entity) =>
        (options?.scopable ?? []).some(
          (item) => item.kind === entity.kind && item.id === entity.id && !item.shared,
        ),
      );
  return { categories, entities, reason: request.reason };
}

/** Whether nothing in the request can be granted here. */
export function nothingGrantable(grant: AssistantScopeRequest): boolean {
  return grant.categories.length === 0 && grant.entities.length === 0;
}
